import { action, computed, makeObservable } from 'mobx';
import { MatchType as MarketType, ItemType, getMarkets } from './markets';
import { couponsStar, couponsMcBookie, CouponType, isCouponId, CouponIdType, couponsDragonBet } from '../coupons';
import { europeanEliteTemplate, scotlandRegex, walesTemplateId, welshFootballTeamTemplate } from '../EventsTemplates';
import { EventsCollectionList } from 'src/domains/sportsbook/state/eventsCollection/EventsCollectionList';
import { EventsCollectionState } from 'src/domains/sportsbook/state/eventsCollection/EventsCollectionState';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import {
    EventsCollectionQueryCompetitionType,
    EventsCollectionQueryModel,
    FilterType,
} from 'src_common/common/websocket2/models/EventsCollectionQueryModel';
import { ModelsState } from 'src_common/common/websocket2/ModelsState';
import { CompetitionId } from 'src_common/common/websocket2/id/WebsocketId';
import { filterByTeams, filterCompetition, filtersComposition } from '../filters';
import { Common } from 'src/domains/common/Common';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { UnifiedEventsListSelectTabState } from './UnifiedEventsListSelectTabState';

export class UnifiedSportEventsListState {
    private readonly configComponents: ConfigComponents;
    private readonly eventsCollection: EventsCollectionState;
    private readonly models: ModelsState;

    private readonly selectTab: UnifiedEventsListSelectTabState;

    public constructor(
        private readonly common: Common,
        private readonly sportId: string,
        private readonly competitionId: CompetitionId | null,
        selectTab: UnifiedEventsListSelectTabState
    ) {
        makeObservable(this);
        this.eventsCollection = EventsCollectionState.get(common);
        this.configComponents = ConfigComponents.get(common);
        this.selectTab = selectTab;
        this.models = common.models;
    }

    public get filter_market(): MarketType | null {
        return this.selectTab.eventsListMarkets.filters_market ?? null;
    }

    @computed public get filter_marketTemplateId(): string | null {
        return this.selectTab.eventsListMarkets.marketTemplateId ?? null;
    }

    @computed public get filter_marketLine(): number | null {
        return this.selectTab.eventsListMarkets.marketLine;
    }

    @computed.struct public get lastSelections(): Array<string> {
        return this.selectTab.eventsListMarkets.lastSelections;
    }

    @computed.struct public get lastSelectionsHeader(): Array<string> {
        return this.selectTab.eventsListMarkets.lastSelectionsHeader;
    }

    public isOutrightsMarket = (): boolean => {
        const coupon = this.selectTab.filter_coupon;
        return coupon === 'custom-outrights-market' || coupon === 'specials';
    };

    public get collection(): EventsCollectionList {
        if (this.isOutrightsMarket()) {
            return this.eventsCollection.listOfSport(this.sportId);
        } else {
            if (this.filter_marketTemplateId === null) {
                return this.eventsCollection.listOfSport(this.sportId);
            }
            return this.eventsCollection.listOfSport(this.sportId, {
                templateId: this.filter_marketTemplateId,
                line: this.filter_marketLine,
            });
        }
    }

    @computed public get isLoading(): boolean {
        return this.collection.isLoading;
    }

    @computed public get collectionQuery(): EventsCollectionQueryModel | null {
        return this.collection.getQuery() ?? null;
    }

    @computed.struct public get competitionsArray(): Array<EventsCollectionQueryCompetitionType> {
        return this.collectionQuery?.competitionsSortById ?? [];
    }

    public filterByCompetition(routeId: CompetitionId): EventsCollectionQueryModel | null {
        return this.visibleEvents?.filterByCompetition((competitionId) => competitionId === routeId.toOldId()) ?? null;
    }

    @computed public get couponForEnv(): CouponType[] {
        const { couponForEnv } = this.configComponents.config;

        switch (couponForEnv) {
            case 'couponsMcBookie': {
                return couponsMcBookie;
            }
            case 'couponsStar': {
                return couponsStar;
            }
            case 'couponsDragonBet': {
                return couponsDragonBet;
            }
        }
    }

    @computed.struct public get translatedCouponsForEnv(): Array<{ name: string; id: string }> {
        return this.couponForEnv.map((filter) => ({
            id: filter.id,
            name: filter.translatedName(LanguagesState.get(this.common).getTranslation),
        }));
    }

    @computed public get marketForEnv(): Array<ItemType> {
        return getMarkets(LanguagesState.get(this.common).getTranslation, this.configComponents.config, this.sportId);
    }

    @action public changeFilter_coupon = (value: string): void => {
        this.changeFilter_market(null);

        if (isCouponId(value)) {
            this.selectTab.filter_coupon = value;
        } else {
            this.selectTab.filter_coupon = 'all';
        }
    };

    @action public changeFilter_market = (id: MarketType | null): void => {
        this.selectTab.eventsListMarkets.filters_market = id;
    };

    public get visibleEvents(): EventsCollectionQueryModel | null {
        return this.collectionQuery;
    }

    @computed public get scottishTemplateId(): Array<number> {
        const competitionsArray = this.competitionsArray;
        const idsArray: Array<number> = [];

        for (const competition of competitionsArray) {
            for (const rx of scotlandRegex) {
                if (rx.test(competition.name.toLowerCase())) {
                    idsArray.push(competition.id);
                }
            }
        }

        return idsArray;
    }

    @computed public get welshTemplateId(): Array<number> {
        const competitionsArray = this.competitionsArray;
        return competitionsArray
            .filter(({ name }) => walesTemplateId.some((templateId) => templateId === name))
            .map(({ id }) => id);
    }

    @computed public get europeanEliteTemplateId(): Array<number> {
        const competitionsArray = this.competitionsArray;
        const idsArray: Array<number> = [];

        for (const competition of competitionsArray) {
            if (europeanEliteTemplate.includes(competition.name)) {
                idsArray.push(competition.id);
            }
        }

        return idsArray;
    }

    public applyFilterCoupon(events: EventsCollectionQueryModel | null): EventsCollectionQueryModel | null {
        if (events === null) {
            return events;
        }

        switch (this.selectTab.filter_coupon) {
            case 'all':
                return events;

            case 'today':
                return events.filter((event) => this.common.serverTime.today.matchMs(event.startTime));

            case 'tomorrow':
                return events.filter((event) => this.common.serverTime.tomorrow.matchMs(event.startTime));

            case 'uk-list':
                return events.filter((event) => event.tags['region'] === 'domestic');

            case 'scottish':
                return events.filterByCompetitionWhenIn(this.scottishTemplateId);

            case 'welsh': {
                const teamFilter: FilterType = filterByTeams(
                    this.sportId,
                    (competition) => competition.events.length > 0,
                    welshFootballTeamTemplate
                );
                const competitionFilter: FilterType = filterCompetition(this.welshTemplateId);
                const filter = filtersComposition([teamFilter, competitionFilter]);
                return events.applyFilter(filter);
            }

            case 'european-elite':
                return events.filterByCompetitionWhenIn(this.europeanEliteTemplateId);

            case 'european-list':
                return events
                    .filter((event) => event.tags['region'] === 'europe')
                    .filterByCompetition((competitionId) => !this.europeanEliteTemplateId.includes(competitionId));

            case 'european-all':
                return events.filter((event) => event.tags['region'] === 'europe');

            case 'row':
                return events.filter((event) => {
                    if (
                        event.tags['region'] === 'africa' ||
                        event.tags['region'] === 'asia' ||
                        event.tags['region'] === 'australasia' ||
                        event.tags['region'] === 'north-america' ||
                        event.tags['region'] === 'south-america'
                    ) {
                        return true;
                    }

                    return false;
                });

            case 'custom-outrights-market': {
                return events.filter((elem) => {
                    console.log('elem', elem);
                    if (elem.template === 'outrights' && elem.tags['outright'] === 'yes') {
                        return true;
                    }
                    return false;
                });
            }

            case 'specials':
                return events.filter((event) => {
                    const evnetModel = this.models.getEvent(event.id);
                    return evnetModel?.tagsFootballSpecial === 'yes';
                });
        }
    }

    @computed public get getFilteredEvent(): EventsCollectionQueryModel | null {
        let events = this.visibleEvents;
        events = this.applyFilterCoupon(events);

        if (this.competitionId !== null) {
            events = this.filterByCompetition(this.competitionId);
        }

        return events;
    }

    @computed public get events(): EventsCollectionQueryModel | null {
        return this.getFilteredEvent;
    }

    @computed public get showEvents(): boolean {
        const filteredEvents = this.getFilteredEvent;
        if (filteredEvents === null) {
            return false;
        }
        return filteredEvents.length > 0;
    }

    @computed public get filter_coupon(): CouponIdType {
        return this.selectTab.filter_coupon;
    }
}
